import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { consolidateErrors } from "../../../helper/utils";
import { FormFieldError, FormControlError } from "../../atoms/FormFiledError";
import { useCreateChatMessageWithFilesMutation } from "../../../store/apiSlice/chatApiSlice";
import { useFormik } from "formik";
import { useNotification } from "../../../hooks/useNotification";
import { useSelector } from "react-redux";
import { useSelectContext } from "../../../hooks/useSelectContext";
import { FormLabel } from "../../atoms/label/label";
import { Upload } from "../../../assets/icons";
import clsx from "clsx";
import { StyledUploadContainer } from "../../atoms/Input/Input.styles";

const UploadFileForm = ({ callback }) => {
  const { id: chatRoomID } =
    useSelector(
      state => state.chat.collabRoom,
      (prev, next) => prev.users === next.users,
    ) || {};

  // Notification hook
  const notify = useNotification();

  // RTK Query hook for creating files
  const [createChatMessageWithFiles, { isLoading }] = useCreateChatMessageWithFilesMutation();

  // Validation schema for form fields
  const validationSchema = Yup.object({
    files: Yup.array()
      .of(
        Yup.mixed().test(
          "fileType",
          "Unsupported file format. Please upload PDF, JPEG, PNG, or WEBP types of files.",
          file => {
            const supportedFormats = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
            return file && supportedFormats.includes(file.type);
          },
        ),
      )
      .required("At least one file is required.")
      .min(1, "At least one file is required."),
    content: Yup.string(),
  });

  // Formik hook for handling form state and submission
  const formik = useFormik({
    initialValues: { content: "", files: [] },
    validationSchema: validationSchema,
    onSubmit: async values => {
      try {
        // Prepare form data
        const formData = new FormData();
        formData.append("chat_room", chatRoomID);
        formData.append("content", values.content);
        Array.from(values.files).forEach(file => {
          formData.append("files", file);
        });

        // Send form data to create files
        const response = await createChatMessageWithFiles(formData).unwrap();
        callback();
        notify({ type: "success", message: response.detail }); // Show success notification
      } catch (error) {
        const err = error?.data;
        if (err.detail) {
          notify({ type: "error", message: err.detail });
        } else {
          const errs = Array.isArray(err) ? consolidateErrors(err) : err;
          // Handle form submission error
          formik.setErrors(errs);
        }
      }
    },
  });

  return (
    <form className="form" encType="multipart/form-data" onSubmit={formik.handleSubmit}>
      {/* Multiple Upload File Input */}
      <div className="row mb-8">
        <label className="fs-6 fw-medium text-gray mb-2 required" htmlFor="files-input">
          Upload Files
        </label>
        <div className="col fv-row">
          <input
            id="files-input"
            className="form-control form-control-solid mb-3 mb-lg-0"
            multiple={true}
            name="files"
            type="file"
            onChange={event => {
              formik.setFieldValue("files", Array.from(event.currentTarget.files));
            }}
          />
          <FormFieldError message={formik.touched.files && formik.errors.files} />
        </div>
      </div>

      {/* Content Text Area */}
      <div className="row mb-8">
        <label className="fs-6 fw-medium text-gray mb-2" htmlFor="content-input">
          Message
        </label>
        <textarea
          {...formik.getFieldProps("content")}
          id="content-input"
          className="form-control form-control-solid mb-3 mb-lg-0 resize-none"
          name="content"
          placeholder="Type a message"
          rows={1}
        />
        <FormFieldError message={formik.touched.content && formik.errors.content} />
      </div>

      {/* Submit Button */}
      <div className="text-center">
        <button
          className="btn btn-primary"
          disabled={
            formik.isSubmitting || !formik.isValid || !formik.touched || !formik.dirty || isLoading
          }
          type="submit"
        >
          {formik.isSubmitting || isLoading ? (
            <span className="d-block indicator-progress">
              Please wait...
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          ) : (
            <span className="indicator-label">Submit</span>
          )}
        </button>
      </div>
    </form>
  );
};

export { UploadFileForm };

export const FormControlImageUploader = ({
  name,
  label = "",
  placeholder = "Upload Your Logo",
  required = false,
  readOnly = false,
  context = "master",
  accept = "image/*",
  ...props
}) => {
  const {
    formik: { values, setFieldValue, touched, errors, setFieldTouched },
  } = useSelectContext(context);

  const fileValue = values[name];
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!fileValue) {
      setPreview(null);
      return;
    }
    if (fileValue instanceof File) {
      const objectUrl = URL.createObjectURL(fileValue);
      setPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
    if (typeof fileValue === "string") {
      setPreview(fileValue);
    }
  }, [fileValue]);

  const handleFileChange = e => {
    const file = e.currentTarget.files?.[0] || null;
    setFieldValue(name, file);
    setFieldTouched(name, true);
  };

  const handleRemove = e => {
    e.preventDefault();
    setFieldValue(name, null);
    setFieldTouched(name, true);
  };

  const hasError = touched[name] && errors[name];

  return (
    <div>
      {label && (
        <FormLabel className={clsx({ req: required })} htmlFor={name}>
          {label}
        </FormLabel>
      )}
      <StyledUploadContainer>
        {preview ? (
          <div className="preview-container">
            <img src={preview} alt="Preview" />
            {!readOnly && (
              <button type="button" className="remove-btn" onClick={handleRemove}>
                &times;
              </button>
            )}
          </div>
        ) : (
          <>
            <input
              type="file"
              id={name}
              accept={accept}
              disabled={readOnly}
              onChange={handleFileChange}
              style={{ display: "none" }}
              {...props}
            />
            <label htmlFor={name} className="upload-label">
              <Upload />
              <span>{placeholder}</span>
            </label>
          </>
        )}
      </StyledUploadContainer>
      <FormControlError id={`${name}-error`} errorMsg={hasError} />
    </div>
  );
};
